import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEOHead from '../SEOHead';
import hqWallpaper from '../../assets/hq-wallpaper.webp';
import logoCircle from '../../assets/logo-circle.jpeg';
import DesktopNavigation from '../Navigation/DesktopNavigation';
import MobileNavigation from '../Navigation/MobileNavigation';
import Footer from '../Footer/Footer';
import { FaChevronLeft, FaChevronRight, FaPlay, FaPause, FaYoutube } from 'react-icons/fa';
import ReactPlayer from 'react-player/youtube';

import resourcefulVideoImg from '../../assets/resourceful-video-content.webp';
import softwareToolsImg from '../../assets/softwre-and-bible-study-digital-tools.webp';
import worshipMusicImg from '../../assets/worship-music.webp';
import biblicalInsightsImg from '../../assets/biblical-insights.webp';
import homeSliderEnglish from '../../assets/home-slider-english.webp';
import homeSliderSpanish from '../../assets/home-slider-spanish.webp';
import bibleImg from '../../assets/bible.webp';

const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const isSpanish = i18n.language && i18n.language.startsWith('es');

  const slides = [
    {
      backgroundImage: hqWallpaper,
      content: (
        <div className="max-w-4xl bg-black/10 backdrop-blur-md p-6 md:p-16 rounded-3xl border border-white/10 shadow-2xl mx-4 flex flex-col items-center text-center">
          <h2 className="text-2xl md:text-5xl font-bold text-white mb-4 md:mb-6 tracking-wide font-oswald uppercase">
            {t('home.slide1Title')}
          </h2>
          <p className="text-sm sm:text-base md:text-2xl text-gray-200 font-light leading-relaxed mb-4 md:mb-6 italic max-w-3xl">
            "{t('home.verseText')}"
          </p>
          <p className="text-sm md:text-xl text-[#05a5b7] font-semibold tracking-wider font-oswald uppercase mb-6 md:mb-8">
            — {t('home.verseRef')}
          </p>
          <Link
            to="/about-us"
            className="inline-flex items-center justify-center bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-8 py-3 md:px-10 md:py-4 rounded-xl font-bold text-sm md:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            {t('home.aboutUs')}
          </Link>
        </div>
      )
    },
    {
      backgroundImage: isSpanish ? homeSliderSpanish : homeSliderEnglish,
      backgroundPosition: "right",
      overlayClass: "bg-transparent",
      content: (
        <div className="max-w-2xl bg-black/10 backdrop-blur-md p-6 md:p-12 rounded-3xl border border-white/10 shadow-2xl mx-4 flex flex-col items-center text-center">
          <h2 className="text-2xl md:text-5xl font-bold text-white mb-4 tracking-wide font-oswald uppercase">
            {t('home.dashboardTitle')}
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-200 leading-relaxed mb-6 md:mb-8 font-light">
            {t('home.dashboardDesc')}
          </p>
          <Link
            to="/software"
            className="inline-flex items-center justify-center bg-[#05a5b7] hover:bg-[#048b9b] text-white px-6 py-2.5 md:px-8 md:py-3.5 rounded-xl font-bold text-sm md:text-md transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            {t('home.seeFeatures')}
          </Link>
        </div>
      )
    },
    {
      backgroundImage: bibleImg,
      overlayClass: "bg-black/10",
      content: (
        <div className="max-w-4xl bg-black/50 backdrop-blur-md p-6 md:p-16 rounded-3xl border border-white/10 shadow-2xl mx-4 flex flex-col items-center text-center">
          <h2 className="text-2xl md:text-5xl font-bold text-white mb-4 md:mb-6 tracking-wide font-oswald uppercase">
            {t('home.slide3Title')}
          </h2>
          <p className="text-sm sm:text-base md:text-2xl text-gray-200 font-light leading-relaxed mb-4 md:mb-6 italic max-w-3xl">
            "{t('home.verse3Text')}"
          </p>
          <p className="text-sm md:text-xl text-[#05a5b7] font-semibold tracking-wider font-oswald uppercase mb-6 md:mb-8">
            — {t('home.verse3Ref')}
          </p>
          <Link
            to="/about-us"
            className="inline-flex items-center justify-center bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-8 py-3 md:px-10 md:py-4 rounded-xl font-bold text-sm md:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            {t('home.aboutUs')}
          </Link>
        </div>
      )
    }
  ];

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    if (!isPlaying) return;

    const timer = setInterval(() => {
      nextSlide();
    }, 10000); // Slower transition duration (10 seconds)

    return () => clearInterval(timer);
  }, [currentSlide, isPlaying]);

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead />
      <DesktopNavigation />
      <MobileNavigation />

      <main className="relative w-full h-screen min-h-[550px] overflow-hidden bg-black">
        {/* Slides */}
        {slides.map((slide: any, index) => (
          <div
            key={index}
            className={`absolute inset-0 w-full h-full transition-all duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
            }`}
          >
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-no-repeat transition-transform duration-1000"
              style={{
                backgroundImage: `url(${slide.backgroundImage})`,
                backgroundPosition: slide.backgroundPosition || 'center',
                transform: index === currentSlide ? 'scale(1)' : 'scale(1.05)',
              }}
            />
            {/* Dark overlay */}
            <div className={`absolute inset-0 z-10 ${slide.overlayClass || 'bg-black/20'}`} />

            {/* Slide Content */}
            <div className="absolute inset-0 z-20 flex items-center justify-center px-4 pt-20">
              {slide.content}
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-3 rounded-full bg-black/30 hover:bg-black/60 text-white/70 hover:text-[#05a5b7] transition-all duration-200 hover:scale-105 active:scale-95"
          aria-label="Previous Slide"
        >
          <FaChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-3 rounded-full bg-black/30 hover:bg-black/60 text-white/70 hover:text-[#05a5b7] transition-all duration-200 hover:scale-105 active:scale-95"
          aria-label="Next Slide"
        >
          <FaChevronRight className="w-5 h-5 md:w-6 md:h-6" />
        </button>

        {/* Dot Indicators & Play/Pause Controls */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center space-x-4 bg-black/20 backdrop-blur-sm px-4 py-2 rounded-full border border-white/10">
          <div className="flex space-x-3">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide 
                    ? 'bg-[#05a5b7] scale-125 shadow-lg' 
                    : 'bg-white/40 hover:bg-white/70'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          <div className="h-4 w-[1px] bg-white/30" />

          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="text-white/70 hover:text-[#05a5b7] transition-all duration-200 focus:outline-none"
            aria-label={isPlaying ? "Pause autoplay" : "Play autoplay"}
            title={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <FaPause className="w-3 h-3" /> : <FaPlay className="w-3 h-3" />}
          </button>
        </div>
      </main>

      {/* Scripture Banner Section */}
      <section className="bg-white py-24 px-6 border-t border-gray-100">
        <div className="max-w-5xl mx-auto flex flex-col items-center text-center">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-[#00a6e0] leading-snug tracking-tight mb-8">
            {t('home.bannerTitle1')}
            <br />
            {t('home.bannerTitle2')}
          </h1>
          
          <div className="w-24 h-24 md:w-28 md:h-28 mb-10 overflow-hidden rounded-full shadow-sm flex items-center justify-center">
            <img 
              src={logoCircle} 
              alt="Christ Reigns logo" 
              className="w-full h-full object-cover" 
            />
          </div>

          <p className="text-lg md:text-2xl text-gray-500 font-medium italic max-w-4xl leading-relaxed">
            {t('home.bannerVerse')}
          </p>
        </div>
      </section>

      {/* Informational Cards Section */}
      <section className="bg-white pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-20">
            {/* Card 1: Resourceful Video Content */}
            <div className="flex flex-col items-center text-center">
              <div className="w-56 h-56 md:w-64 md:h-64 mb-8 overflow-hidden rounded-full shadow-md border-2 border-gray-100 flex items-center justify-center">
                <img 
                  src={resourcefulVideoImg} 
                  alt="Resourceful Video Content" 
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" 
                />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 font-sans">
                {t('home.cards.videoTitle')}
              </h3>
              <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-6 max-w-lg font-light">
                {t('home.cards.videoDesc')}
              </p>
              <a
                href="https://www.youtube.com/@ChristReignsM"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#38bdf8] hover:bg-[#0ea5e9] text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105 shadow-sm inline-flex items-center"
              >
                {t('home.cards.exploreMore')}
              </a>
            </div>

            {/* Card 2: Software and Bible Study Digital Tools */}
            <div className="flex flex-col items-center text-center">
              <div className="w-56 h-56 md:w-64 md:h-64 mb-8 overflow-hidden rounded-full shadow-md border-2 border-gray-100 flex items-center justify-center">
                <img 
                  src={softwareToolsImg} 
                  alt="Software and Bible Study Digital Tools" 
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" 
                />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 font-sans">
                {t('home.cards.softwareTitle')}
              </h3>
              <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-6 max-w-lg font-light">
                {t('home.cards.softwareDesc')}
              </p>
              <Link
                to="/software"
                className="bg-[#38bdf8] hover:bg-[#0ea5e9] text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105 shadow-sm inline-flex items-center"
              >
                {t('home.cards.exploreMore')}
              </Link>
            </div>

            {/* Card 3: Worship Music With a Powerful Message */}
            <div className="flex flex-col items-center text-center">
              <div className="w-56 h-56 md:w-64 md:h-64 mb-8 overflow-hidden rounded-full shadow-md border-2 border-gray-100 flex items-center justify-center">
                <img 
                  src={worshipMusicImg} 
                  alt="Worship Music With a Powerful Message" 
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" 
                />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 font-sans">
                {t('home.cards.musicTitle')}
              </h3>
              <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-6 max-w-lg font-light">
                {t('home.cards.musicDesc')}
              </p>
              <a
                href="https://www.youtube.com/@christ-reigns-media-music"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#38bdf8] hover:bg-[#0ea5e9] text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105 shadow-sm inline-flex items-center"
              >
                {t('home.cards.exploreMore')}
              </a>
            </div>

            {/* Card 4: Biblical Insights, Apologetics & Daily Reflections */}
            <div className="flex flex-col items-center text-center">
              <div className="w-56 h-56 md:w-64 md:h-64 mb-8 overflow-hidden rounded-full shadow-md border-2 border-gray-100 flex items-center justify-center">
                <img 
                  src={biblicalInsightsImg} 
                  alt="Biblical Insights, Apologetics & Daily Reflections" 
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" 
                />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 font-sans">
                {t('home.cards.insightsTitle')}
              </h3>
              <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-6 max-w-lg font-light">
                {t('home.cards.insightsDesc')}
              </p>
              <Link
                to="/articles"
                className="bg-[#38bdf8] hover:bg-[#0ea5e9] text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105 shadow-sm inline-flex items-center"
              >
                {t('home.cards.exploreMore')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Music Section */}
      <section id="featured-music" className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white py-24 px-6 relative overflow-hidden border-t border-b border-white/5">
        {/* Glow Effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-600/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <span className="text-xs uppercase tracking-widest font-bold text-[#38bdf8] mb-3 bg-[#38bdf8]/10 px-3 py-1.5 rounded-full inline-block">
            {t('musicSection.badge')}
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent font-sans">
            {t('musicSection.title')}
          </h2>
          <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto mb-16 font-light leading-relaxed">
            {t('musicSection.subtitle')}
          </p>

          {/* Video Grid */}
          <div className="grid md:grid-cols-3 gap-8 md:gap-10">
            {/* Synthwave Video */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 overflow-hidden hover:border-[#38bdf8]/40 hover:shadow-[0_0_40px_rgba(56,189,248,0.15)] transition-all duration-500 group flex flex-col">
              <div className="relative w-full aspect-video overflow-hidden rounded-t-2xl bg-black">
                <ReactPlayer
                  url="https://www.youtube.com/watch?v=3N39BTFSGzM"
                  width="100%"
                  height="100%"
                  controls={true}
                  light={true}
                  playIcon={
                    <div className="absolute inset-0 flex items-center justify-center bg-black/45 group-hover:bg-black/25 transition-all duration-300 animate-fade-in">
                      <div className="w-16 h-16 rounded-full bg-[#38bdf8]/90 group-hover:bg-[#38bdf8] text-white flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-all duration-300">
                        <FaPlay className="w-5 h-5 translate-x-0.5" />
                      </div>
                    </div>
                  }
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-left">
                <span className="text-[10px] uppercase font-bold tracking-wider text-pink-400 bg-pink-500/10 border border-pink-500/20 px-2.5 py-1 rounded-md self-start">
                  Synthwave
                </span>
                <h3 className="text-lg font-bold text-white mb-2 mt-3 group-hover:text-[#38bdf8] transition-colors duration-300">
                  {t('musicSection.synthwaveTitle')}
                </h3>
                <p className="text-gray-400 text-sm font-light leading-relaxed mb-6 flex-grow">
                  {t('musicSection.synthwaveDesc')}
                </p>
                <a
                  href="https://www.youtube.com/watch?v=3N39BTFSGzM"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-300 group-hover:text-white bg-white/5 group-hover:bg-[#38bdf8] border border-white/10 group-hover:border-transparent py-2.5 rounded-xl transition-all duration-300"
                >
                  <FaYoutube className="w-4 h-4" />
                  {t('musicSection.youtubeLink')}
                </a>
              </div>
            </div>

            {/* Hard Rock Video */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 overflow-hidden hover:border-[#38bdf8]/40 hover:shadow-[0_0_40px_rgba(56,189,248,0.15)] transition-all duration-500 group flex flex-col">
              <div className="relative w-full aspect-video overflow-hidden rounded-t-2xl bg-black">
                <ReactPlayer
                  url="https://www.youtube.com/watch?v=rjgtIWOxSUo"
                  width="100%"
                  height="100%"
                  controls={true}
                  light={true}
                  playIcon={
                    <div className="absolute inset-0 flex items-center justify-center bg-black/45 group-hover:bg-black/25 transition-all duration-300 animate-fade-in">
                      <div className="w-16 h-16 rounded-full bg-[#38bdf8]/90 group-hover:bg-[#38bdf8] text-white flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-all duration-300">
                        <FaPlay className="w-5 h-5 translate-x-0.5" />
                      </div>
                    </div>
                  }
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-left">
                <span className="text-[10px] uppercase font-bold tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2.5 py-1 rounded-md self-start">
                  Hard Rock
                </span>
                <h3 className="text-lg font-bold text-white mb-2 mt-3 group-hover:text-[#38bdf8] transition-colors duration-300">
                  {t('musicSection.hardRockTitle')}
                </h3>
                <p className="text-gray-400 text-sm font-light leading-relaxed mb-6 flex-grow">
                  {t('musicSection.hardRockDesc')}
                </p>
                <a
                  href="https://www.youtube.com/watch?v=rjgtIWOxSUo&list=PLvB1e08cXGIli7dK0TaCOrRzlAfTaTk8h&index=8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-300 group-hover:text-white bg-white/5 group-hover:bg-[#38bdf8] border border-white/10 group-hover:border-transparent py-2.5 rounded-xl transition-all duration-300"
                >
                  <FaYoutube className="w-4 h-4" />
                  {t('musicSection.youtubeLink')}
                </a>
              </div>
            </div>

            {/* Metal Video */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 overflow-hidden hover:border-[#38bdf8]/40 hover:shadow-[0_0_40px_rgba(56,189,248,0.15)] transition-all duration-500 group flex flex-col">
              <div className="relative w-full aspect-video overflow-hidden rounded-t-2xl bg-black">
                <ReactPlayer
                  url="https://www.youtube.com/watch?v=WyLx4vHodNY"
                  width="100%"
                  height="100%"
                  controls={true}
                  light={true}
                  playIcon={
                    <div className="absolute inset-0 flex items-center justify-center bg-black/45 group-hover:bg-black/25 transition-all duration-300 animate-fade-in">
                      <div className="w-16 h-16 rounded-full bg-[#38bdf8]/90 group-hover:bg-[#38bdf8] text-white flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-all duration-300">
                        <FaPlay className="w-5 h-5 translate-x-0.5" />
                      </div>
                    </div>
                  }
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-left">
                <span className="text-[10px] uppercase font-bold tracking-wider text-red-400 bg-red-500/10 border border-red-500/20 px-2.5 py-1 rounded-md self-start">
                  Metal
                </span>
                <h3 className="text-lg font-bold text-white mb-2 mt-3 group-hover:text-[#38bdf8] transition-colors duration-300">
                  {t('musicSection.metalTitle')}
                </h3>
                <p className="text-gray-400 text-sm font-light leading-relaxed mb-6 flex-grow">
                  {t('musicSection.metalDesc')}
                </p>
                <a
                  href="https://www.youtube.com/watch?v=WyLx4vHodNY&list=PLvB1e08cXGIlRDfKkpbz9mxsVrntjDT9r&index=6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-300 group-hover:text-white bg-white/5 group-hover:bg-[#38bdf8] border border-white/10 group-hover:border-transparent py-2.5 rounded-xl transition-all duration-300"
                >
                  <FaYoutube className="w-4 h-4" />
                  {t('musicSection.youtubeLink')}
                </a>
              </div>
            </div>
          </div>

          <div className="text-center mt-16">
            <a
              href="https://www.youtube.com/@christ-reigns-media-music"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#38bdf8] hover:bg-[#0ea5e9] text-white px-8 py-3 rounded-lg font-bold text-sm transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
            >
              <FaYoutube className="w-5 h-5" />
              {t('footer.ourMinistry.youtubeChannels')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
